import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from "react";

/*
 * Pointer-driven drag and drop for lists that aren't flat (rail folders,
 * channel categories). Mouse and pen start after a small movement; touch starts
 * with a long press, so ordinary swipes still scroll. The consumer supplies a
 * hit test that turns a pointer position into a drop target and renders its own
 * indicator from `drag`. Escape cancels. The click that ends a drag is swallowed.
 */

export interface DragState<S, T> {
  source: S;
  x: number;
  y: number;
  target: T | null;
}

interface Pending<S> {
  source: S;
  pointerId: number;
  type: string;
  startX: number;
  startY: number;
  x: number;
  y: number;
  armed: boolean;
  started: boolean;
  timer: number;
}

const MOUSE_THRESHOLD = 4;
const TOUCH_SLOP = 8;
const LONG_PRESS = 250;
/** Holding still this long belongs to the long-press context menu (Radix opens at 700ms). */
const MENU_PRESS = 650;
const EDGE = 36;

export function usePointerDrag<S, T>({
  hitTest,
  onDrop,
  scrollRef,
  enabled = true,
}: {
  hitTest: (x: number, y: number, source: S) => T | null;
  onDrop: (source: S, target: T) => void;
  scrollRef?: RefObject<HTMLElement | null>;
  enabled?: boolean;
}) {
  const [drag, setDrag] = useState<DragState<S, T> | null>(null);
  /** Touch: held long enough that moving will drag (the item can show it's been picked up). */
  const [armed, setArmed] = useState<S | null>(null);
  const opts = useRef({ hitTest, onDrop, scrollRef });
  opts.current = { hitTest, onDrop, scrollRef };
  const p = useRef<Pending<S> | null>(null);
  const cleanup = useRef<() => void>(() => {});

  useEffect(() => () => cleanup.current(), []);

  const pointerDown = useCallback(
    (e: ReactPointerEvent, source: S) => {
      if (!enabled || p.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey) return; // let modified clicks open links in new tabs etc.
      const touch = e.pointerType === "touch";
      const pend: Pending<S> = { source, pointerId: e.pointerId, type: e.pointerType, startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY, armed: !touch, started: false, timer: 0 };
      p.current = pend;
      const el = e.currentTarget as Element;
      let raf = 0;

      const update = () => {
        const target = opts.current.hitTest(pend.x, pend.y, pend.source);
        setDrag({ source: pend.source, x: pend.x, y: pend.y, target });
      };

      // Keep scrolling while the pointer rests near the container's edges.
      const tick = () => {
        raf = 0;
        const sc = opts.current.scrollRef?.current;
        if (!pend.started || !sc) return;
        const r = sc.getBoundingClientRect();
        const dy = pend.y < r.top + EDGE ? -Math.ceil((r.top + EDGE - pend.y) / 4) : pend.y > r.bottom - EDGE ? Math.ceil((pend.y - (r.bottom - EDGE)) / 4) : 0;
        if (!dy) return;
        const before = sc.scrollTop;
        sc.scrollTop += dy;
        if (sc.scrollTop !== before) update();
        raf = requestAnimationFrame(tick);
      };

      const start = () => {
        pend.started = true;
        setArmed(null);
        try {
          el.setPointerCapture(pend.pointerId); // keep receiving moves and the release outside the window
        } catch {
          /* pointer already gone */
        }
        window.getSelection()?.removeAllRanges();
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
        update();
      };

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== pend.pointerId) return;
        pend.x = ev.clientX;
        pend.y = ev.clientY;
        const dist = Math.hypot(pend.x - pend.startX, pend.y - pend.startY);
        if (!pend.started) {
          if (!pend.armed) {
            if (dist > TOUCH_SLOP) finish(false); // a scroll or swipe, not a drag
            return;
          }
          if (dist <= (touch ? 2 : MOUSE_THRESHOLD)) return;
          start();
        }
        update();
        if (!raf) raf = requestAnimationFrame(tick);
      };
      const up = (ev: PointerEvent) => {
        if (ev.pointerId !== pend.pointerId) return;
        finish(pend.started);
      };
      const cancel = (ev: PointerEvent) => {
        if (ev.pointerId === pend.pointerId) finish(false);
      };
      const key = (ev: KeyboardEvent) => {
        if (ev.key === "Escape" && pend.started) {
          ev.preventDefault();
          ev.stopPropagation();
          finish(false, true);
        }
      };
      // Once armed on touch, stop the page from scrolling under the finger.
      const touchMove = (ev: TouchEvent) => {
        if (pend.armed || pend.started) ev.preventDefault();
      };
      // A long press that hasn't moved belongs to the context menu; one that's dragging doesn't.
      const contextMenu = (ev: MouseEvent) => {
        if (pend.started) {
          ev.preventDefault();
          ev.stopPropagation();
        } else if (touch) finish(false);
      };

      function finish(drop: boolean, cancelled = false) {
        const wasStarted = pend.started;
        if (drop && !cancelled) {
          const target = opts.current.hitTest(pend.x, pend.y, pend.source);
          if (target !== null) opts.current.onDrop(pend.source, target);
        }
        cleanup.current();
        if (wasStarted) {
          // The click that follows pointerup would otherwise follow the link.
          const swallow = (ce: MouseEvent) => {
            ce.preventDefault();
            ce.stopPropagation();
          };
          window.addEventListener("click", swallow, { capture: true, once: true });
          setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 50);
        }
      }

      if (touch) {
        pend.timer = window.setTimeout(() => {
          pend.armed = true;
          setArmed(source);
          navigator.vibrate?.(10);
          // Still holding without moving: hand over to the long-press context menu.
          pend.timer = window.setTimeout(() => !pend.started && finish(false), MENU_PRESS - LONG_PRESS);
        }, LONG_PRESS);
      }

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("keydown", key, true);
      window.addEventListener("touchmove", touchMove, { passive: false });
      window.addEventListener("contextmenu", contextMenu, true);
      cleanup.current = () => {
        clearTimeout(pend.timer);
        if (raf) cancelAnimationFrame(raf);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        window.removeEventListener("keydown", key, true);
        window.removeEventListener("touchmove", touchMove);
        window.removeEventListener("contextmenu", contextMenu, true);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        p.current = null;
        setDrag(null);
        setArmed(null);
        cleanup.current = () => {};
      };
    },
    [enabled],
  );

  return { drag, armed, pointerDown };
}

/** Rows under a container, measured now. */
export function measure(root: HTMLElement | null, selector: string) {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>(selector)].map((el) => ({ el, rect: el.getBoundingClientRect() }));
}

/** The row containing y, else the nearest one. */
export function rowAt<R extends { rect: DOMRect }>(rows: R[], y: number): R | null {
  let best: R | null = null;
  let bestD = Infinity;
  for (const r of rows) {
    if (y >= r.rect.top && y <= r.rect.bottom) return r;
    const d = Math.min(Math.abs(y - r.rect.top), Math.abs(y - r.rect.bottom));
    if (d < bestD) {
      bestD = d;
      best = r;
    }
  }
  return best;
}
